import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';

export interface UseCaptureReturnToModuleOptions {
  /**
   * Whether the hook should process and return the returnToModule URL
   * @default true
   */
  enabled?: boolean;
}

export interface UseCaptureReturnToModuleResult {
  /**
   * The decoded URL to return to the module
   * Will be null if:
   * - No returnToModule query param exists
   * - enabled is false
   * - returnToModule param is not a string
   */
  returnToModuleUrl: string | null;

  /**
   * Whether the URL capture process is currently happening
   */
  isCapturing: boolean;
}

/**
 * Hook to capture and manage the "return to module" functionality.
 *
 * When a page is accessed with `?captureReturnUrl=true`, this hook will:
 * 1. Capture the document.referrer (the page the user came from)
 * 2. Replace the URL to convert `captureReturnUrl=true` to `returnToModule=<encoded-referrer>`
 * 3. Provide the decoded returnToModule URL for consumers to use
 *
 * @example
 * ```tsx
 * const { returnToModuleUrl } = useCaptureReturnToModule();
 *
 * return (
 *   <>
 *     {returnToModuleUrl && (
 *       <Button linkButton={{ url: returnToModuleUrl }}>
 *         Return to Module
 *       </Button>
 *     )}
 *   </>
 * );
 * ```
 */
export function useCaptureReturnToModule(
  options: UseCaptureReturnToModuleOptions = {},
): UseCaptureReturnToModuleResult {
  const { enabled = true } = options;
  const { query, asPath, replace } = useRouter();
  const [isCapturing, setIsCapturing] = useState(false);

  // Effect to handle captureReturnUrl → returnToModule conversion
  useEffect(() => {
    const captureReturnUrlQueryParam = query.captureReturnUrl;

    if (captureReturnUrlQueryParam && captureReturnUrlQueryParam === 'true') {
      setIsCapturing(true);

      // Use asPath and strip query parameters to get the actual resolved path
      const pathWithoutQuery = asPath.split('?')[0];

      replace(
        {
          href: pathWithoutQuery,
          query: { returnToModule: encodeURIComponent(document.referrer) },
        },
        undefined,
        { shallow: true },
      )
        .then(() => {
          setIsCapturing(false);
        })
        .catch(() => {
          setIsCapturing(false);
        });
    }
  }, [query.captureReturnUrl, asPath, replace]);

  // Memoized computation of the decoded returnToModule URL
  const returnToModuleUrl = useMemo(() => {
    if (!enabled) {
      return null;
    }

    const returnToModuleQueryParam = query.returnToModule;

    if (!returnToModuleQueryParam || typeof returnToModuleQueryParam !== 'string') {
      return null;
    }

    return decodeURIComponent(returnToModuleQueryParam);
  }, [enabled, query.returnToModule]);

  return {
    returnToModuleUrl,
    isCapturing,
  };
}
